import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, Menu, MenuItem, Grid, CircularProgress, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../provider/authProvider';
import { useAccount } from '../provider/accountProvider';
import { StrategyCard } from '../Components/Strategy';
import { useSignalRContext } from '../SignalR/useSignalR';
import { executeManualOrder, resetStrategySnapshot } from '../api/strategyApi';
// FE-9: Import dashboard type utilities
import { isAnalyticsDashboard, DASHBOARD_TYPES } from '../utils/dashboardTypeUtils';
// QuantX Analytics Panel - Using DataDensePanel for maximum data density
import { DataDensePanel, QAIChatPanel } from '../Components/QuantX';
import { QUANTX_CONFIG } from '../config';
// INT-1: Import AI Context API for Q.AI integration
import { getAIContext } from '../api/quantxApi';

// FE-9: Dashboard type constant for this page
const CURRENT_DASHBOARD_TYPE = DASHBOARD_TYPES.ANALYTICS; // 'Algo.02'

/**
 * AlgoAnalyticsDashboard - Multi-Strategy Analytics Dashboard
 *
 * Displays all active user strategies in a responsive grid layout
 * with real-time updates from SignalR and historical data from DataFeed API.
 *
 * Features:
 * - Responsive grid (xs:1col sm:2col md:3col lg:4col xl:6col)
 * - Account filtering
 * - Real-time PnL updates
 * - Strategy control buttons (BUY/SELL/CLOSE/RESET)
 * - MT5-style charts with percentile bands
 */
const AlgoAnalyticsDashboard = () => {
    const navigate = useNavigate();
    const { token, currentUser } = useAuth();
    const userId = currentUser?.userId;
    const { strategyDashboardList } = useSignalRContext(); // MULTI-STRATEGY: Use full array instead of single object

    // Use centralized account management from AccountProvider
    const { accounts: userAccounts, selectedAccount, setSelectedAccount, getAccountNumber } = useAccount();

    // State management
    const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [strategies, setStrategies] = useState([]);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [currentTime, setCurrentTime] = useState(new Date());
    // QuantX Analytics Panel state
    const [showQuantXPanel, setShowQuantXPanel] = useState(true);
    const [quantXSymbol, setQuantXSymbol] = useState(QUANTX_CONFIG.defaultSymbol);

    // Q.AI Chat Panel state
    const [showQAIChat, setShowQAIChat] = useState(false);
    const [qaiMinimized, setQAIMinimized] = useState(false);
    const [qaiMaximized, setQAIMaximized] = useState(false);
    // INT-1: AI Context for Q.AI chat integration
    const [aiContext, setAIContext] = useState(null);

    // Live clock update (every second)
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // INT-1: Fetch AI Context when Q.AI chat is opened or symbol changes
    useEffect(() => {
        const fetchAIContext = async () => {
            if (!showQAIChat || !token || !quantXSymbol) {
                return;
            }

            try {
                console.log('[AlgoAnalyticsDashboard] Fetching AI context for', quantXSymbol);
                const context = await getAIContext(token, quantXSymbol);
                setAIContext(context);
                console.log('[AlgoAnalyticsDashboard] AI Context loaded:', {
                    hasContext: !!context?.contextText,
                    suggestedQuestions: context?.suggestedQuestions?.length || 0
                });
            } catch (error) {
                console.warn('[AlgoAnalyticsDashboard] Failed to fetch AI context:', error);
                setAIContext(null);
            }
        };

        fetchAIContext();
    }, [showQAIChat, quantXSymbol, token]);

    // Subscribe to SignalR strategy data (Task 2.1 - Real Data Integration + Dashboard Routing + Security + MULTI-STRATEGY)
    useEffect(() => {
        // Initial loading state
        setLoading(true);

        // MULTI-STRATEGY: Process all strategies in the array
        if (strategyDashboardList && strategyDashboardList.length > 0) {
            // Extract current account number
            const currentAccountNumber = selectedAccount?.accountNumber ||
                                        selectedAccount?.account_number ||
                                        (typeof selectedAccount === 'number' ? selectedAccount : null);

            console.log(`[AlgoAnalyticsDashboard] 🔍 Filtering ${strategyDashboardList.length} strategies for Algo.02 dashboard`);

            // Filter strategies: Dashboard Type = Algo.02 AND Account Ownership
            const filteredStrategies = strategyDashboardList.filter(strategy => {
                const strategyId = strategy.id || strategy.strategyId || strategy.StrategyId || strategy.strategyID;

                const strategyAccountNumber = strategy.accountNumber || strategy.AccountNumber || strategy.account_number;
                // FIX: Default to Algo.01 (Legacy) - strategies without dashboardType should NOT appear here
                const dashboardType = strategy.dashboardType || strategy.DashboardType || 'Algo.01';

                // 🔒 FILTER 1: Check dashboard type (Analytics Dashboard = Algo.02 OR Algo-2)
                // FE-9: Use utility function for consistent format handling
                const isAnalytics = isAnalyticsDashboard(dashboardType);

                if (!isAnalytics) {
                    console.log(`[AlgoAnalyticsDashboard] ⚠️ FILTERED: Strategy ${strategyId} assigned to ${dashboardType}, not Analytics Dashboard (Algo-2/Algo.02)`);
                    return false;
                }

                // 🔒 FILTER 2: Check account ownership (only if both account numbers available)
                // If no account selected yet, skip account filter - dashboard type filter is enough
                if (currentAccountNumber && strategyAccountNumber) {
                    if (strategyAccountNumber?.toString() !== currentAccountNumber?.toString()) {
                        console.log(`[AlgoAnalyticsDashboard] ⚠️ Account mismatch: strategy=${strategyAccountNumber} vs selected=${currentAccountNumber}`);
                        return false;
                    }
                }

                // ✅ ALL FILTERS PASSED
                console.log(`[AlgoAnalyticsDashboard] ✅ Strategy ${strategyId} passed filters - DISPLAYING`);
                return true;
            });

            console.log(`[AlgoAnalyticsDashboard] 📊 Displaying ${filteredStrategies.length} strategies (filtered from ${strategyDashboardList.length})`);

            setStrategies(filteredStrategies);
            setLoading(false);
            setLastRefresh(new Date());
        } else {
            // No data yet - show loading
            console.log('[AlgoAnalyticsDashboard] ⏳ Waiting for SignalR strategy data...');
            console.log('[AlgoAnalyticsDashboard] 💡 TIP: Make sure strategies are STARTED and assigned to dashboardType: "Algo.02"');

            // Set timeout to show "no data" state after 5 seconds
            const timeout = setTimeout(() => {
                if (!strategyDashboardList || strategyDashboardList.length === 0) {
                    setLoading(false);
                    setStrategies([]);
                }
            }, 5000);

            return () => clearTimeout(timeout);
        }
    }, [strategyDashboardList, selectedAccount]);

    // Account selector handlers
    const handleAccountClick = (event) => {
        setAccountMenuAnchor(event.currentTarget);
    };

    const handleAccountClose = () => {
        setAccountMenuAnchor(null);
    };

    const handleAccountSelect = (account) => {
        setSelectedAccount(account); // AccountProvider handles localStorage automatically
        handleAccountClose();
    };

    // Refresh handler
    const handleRefresh = useCallback(() => {
        setLastRefresh(new Date());
        // TODO: Re-fetch strategies from API when Task #1 is complete
        console.log('Refreshing strategies...');
    }, []);

    // Format price with symbol-specific precision
    // Aligned with DashboardPage.js getDecimalPrecision for consistency
    const formatWithPrecision = (value, symbol) => {
        if (value === null || value === undefined) return '-';
        if (typeof value !== 'number') return value;

        // Determine precision based on symbol type
        const getDecimalPrecision = (sym) => {
            if (!sym) return 5; // Default to 5 for forex

            const symbolUpper = sym.toUpperCase();

            // Precious metals: 2 decimals
            if (symbolUpper.includes('XAU') || symbolUpper.includes('GOLD') ||
                symbolUpper.includes('XAG') || symbolUpper.includes('SILVER') ||
                symbolUpper.includes('XPT') || symbolUpper.includes('XPD')) {
                return 2;
            }

            // JPY pairs: 3 decimals
            if (symbolUpper.includes('JPY')) {
                return 3;
            }

            // Standard forex (6-char pairs): 5 decimals
            if (symbolUpper.length === 6 && /^[A-Z]{6}$/.test(symbolUpper)) {
                return 5;
            }

            // Crypto: 2 decimals
            if (symbolUpper.includes('BTC') || symbolUpper.includes('ETH')) {
                return 2;
            }

            // Default: 5 decimals for forex spreads
            return 5;
        };

        const precision = getDecimalPrecision(symbol);
        return value.toFixed(precision);
    };

    // Strategy control button handlers - ✅ FIX-2: Wired to API
    const handleBuySpread = useCallback(async (strategyId) => {
        try {
            // Find strategy in list
            const strategy = strategies.find(s => (s.strategyId || s.id) === strategyId);
            if (!strategy) {
                toast.error('Strategy not found', { position: "top-right", autoClose: 2000 });
                return;
            }

            const futuresSymbol = strategy.marketData?.futuresSymbol;
            const spotSymbol = strategy.marketData?.spotSymbol;
            const buyLotSize = parseFloat(strategy.parameters?.['GridManagement.BuyBaseLotSize']) || 0.01;

            if (!futuresSymbol || !spotSymbol) {
                toast.error('Symbol information not available', { position: "top-right", autoClose: 2000 });
                return;
            }

            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const orders = [
                {
                    symbolName: futuresSymbol,
                    tradeSide: "Buy",
                    quantity: buyLotSize
                },
                {
                    symbolName: spotSymbol,
                    tradeSide: "Sell",
                    quantity: buyLotSize
                }
            ];

            const result = await executeManualOrder(token, strategyId, "OpenBuySpread", accountNumber, orders);

            if (result.success) {
                toast.success('Buy Spread opened successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to open Buy Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            console.error('Error executing Buy Spread:', error);
            toast.error('Failed to execute Buy Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [strategies, token, selectedAccount]);

    const handleSellSpread = useCallback(async (strategyId) => {
        try {
            // Find strategy in list
            const strategy = strategies.find(s => (s.strategyId || s.id) === strategyId);
            if (!strategy) {
                toast.error('Strategy not found', { position: "top-right", autoClose: 2000 });
                return;
            }

            const futuresSymbol = strategy.marketData?.futuresSymbol;
            const spotSymbol = strategy.marketData?.spotSymbol;
            const sellLotSize = parseFloat(strategy.parameters?.['GridManagement.SellBaseLotSize']) || 0.01;

            if (!futuresSymbol || !spotSymbol) {
                toast.error('Symbol information not available', { position: "top-right", autoClose: 2000 });
                return;
            }

            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const orders = [
                {
                    symbolName: futuresSymbol,
                    tradeSide: "Sell",
                    quantity: sellLotSize
                },
                {
                    symbolName: spotSymbol,
                    tradeSide: "Buy",
                    quantity: sellLotSize
                }
            ];

            const result = await executeManualOrder(token, strategyId, "OpenSellSpread", accountNumber, orders);

            if (result.success) {
                toast.success('Sell Spread opened successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to open Sell Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            console.error('Error executing Sell Spread:', error);
            toast.error('Failed to execute Sell Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [strategies, token, selectedAccount]);

    const handleCloseBuy = useCallback(async (strategyId) => {
        try {
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseLastBuySpread", accountNumber);

            if (result.success) {
                toast.success('Last Buy Spread closed successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to close Buy Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            console.error('Error closing Buy Spread:', error);
            toast.error('Failed to close Buy Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [token, selectedAccount]);

    const handleCloseSell = useCallback(async (strategyId) => {
        try {
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseLastSellSpread", accountNumber);

            if (result.success) {
                toast.success('Last Sell Spread closed successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to close Sell Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            console.error('Error closing Sell Spread:', error);
            toast.error('Failed to close Sell Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [token, selectedAccount]);

    const handleCloseAll = useCallback(async (strategyId) => {
        try {
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseAll", accountNumber);

            if (result.success) {
                toast.success('All positions closed successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to close all positions', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            console.error('Error closing all positions:', error);
            toast.error('Failed to close all positions', { position: "top-right", autoClose: 2000 });
        }
    }, [token, selectedAccount]);

    const handleResetStrategy = useCallback(async (strategyId) => {
        try {
            const result = await resetStrategySnapshot(token, strategyId);

            if (result.success) {
                toast.success('Strategy snapshot reset successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to reset strategy snapshot', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            console.error('Error resetting strategy:', error);
            toast.error('Failed to reset strategy snapshot', { position: "top-right", autoClose: 2000 });
        }
    }, [token]);

    const handleApplyParameters = (strategyId, params) => {
        console.log(`[AlgoAnalyticsDashboard] APPLY PARAMETERS for strategy ${strategyId}`, params);
        // TODO: Call updateStrategyParameters API
    };

    // Calculate total P&L from all strategies
    const getTotalPnL = () => {
        if (!strategies || strategies.length === 0) return 0;
        return strategies.reduce((total, strategy) => {
            const pnl = strategy.position?.accumulativeTP || 0;
            return total + pnl;
        }, 0);
    };

    const totalPnL = getTotalPnL();
    const pnlColor = totalPnL >= 0 ? '#22c55e' : '#ef4444';
    const pnlArrow = totalPnL >= 0 ? '↑' : '↓';

    // Get current trading session based on GMT time
    const getCurrentSession = () => {
        const hour = currentTime.getUTCHours();
        if (hour >= 22 || hour < 7) return { name: 'SYDNEY', color: '#a855f7' };
        if (hour >= 0 && hour < 9) return { name: 'TOKYO', color: '#fbbf24' };
        if (hour >= 8 && hour < 16.5) return { name: 'LONDON', color: '#f59e0b' }; // Changed from cyan to amber
        if (hour >= 13 && hour < 22) return { name: 'NEW YORK', color: '#22c55e' };
        return { name: 'CLOSED', color: '#9ca3af' };
    };

    const currentSession = getCurrentSession();

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                background: '#0f0f0f',
                color: '#ffffff',
                padding: 0,
                overflow: 'auto'
            }}
        >
            {/* Top Accent Strip - CQG Red */}
            <Box sx={{
                height: '2px',
                background: '#dc2626',
                boxShadow: '0 2px 12px rgba(220, 38, 38, 0.6)'
            }}></Box>

            {/* Header Bar - CQG/Bloomberg Style */}
            <Box
                sx={{
                    background: '#0a0a0a',
                    borderBottom: '2px solid #dc2626',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                    padding: '6px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 3,
                    minHeight: '52px'
                }}
            >
                {/* LEFT: Compact Logo + DOMINANT Brand */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Logo Box - QuantX */}
                    <Box sx={{
                        background: '#1a1a1a',
                        border: '2px solid #dc2626',
                        padding: '4px 8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.2,
                        minWidth: '68px',
                        boxShadow: '0 0 10px rgba(220, 38, 38, 0.4)'
                    }}>
                        <Typography sx={{
                            fontWeight: 900,
                            fontSize: '13px',
                            color: '#dc2626',
                            fontFamily: 'Arial, sans-serif',
                            letterSpacing: '1.5px',
                            lineHeight: 1
                        }}>
                            QuantX
                        </Typography>
                        <Typography sx={{
                            fontSize: '8px',
                            color: '#6b7280',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            lineHeight: 1
                        }}>
                            ANALYTICS
                        </Typography>
                    </Box>

                    {/* DOMINANT Brand Text + Sleek Slogan */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Typography sx={{
                            fontWeight: 900,
                            fontSize: '42px',
                            color: '#3deaff',
                            fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
                            letterSpacing: '0.8px',
                            lineHeight: 0.95,
                            textShadow: '0 2px 8px rgba(61, 234, 255, 0.15)'
                        }}>
                            QTradeView
                        </Typography>
                        <Typography sx={{
                            fontSize: '11px',
                            color: '#8b9cb5',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            fontFamily: '"Inter", "Roboto", system-ui, sans-serif',
                            lineHeight: 1,
                            fontStyle: 'normal',
                            textTransform: 'none',
                            opacity: 0.9
                        }}>
                            Artificial Intelligence SolutionsX
                        </Typography>
                    </Box>
                </Box>

                {/* CENTER: Status Bar with Icons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'center' }}>
                    {/* Connection */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '14px' }}>🟢</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#22c55e', fontWeight: 700, fontFamily: 'monospace' }}>
                            LIVE
                        </Typography>
                    </Box>

                    <Box sx={{ width: '1px', height: '16px', backgroundColor: '#374151' }} />

                    {/* Strategies */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '14px' }}>📊</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#9ca3af', fontWeight: 600, fontFamily: 'monospace' }}>
                            {strategies.length} ACTIVE
                        </Typography>
                    </Box>

                    <Box sx={{ width: '1px', height: '16px', backgroundColor: '#374151' }} />

                    {/* Session */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '14px' }}>🌐</Typography>
                        <Typography sx={{ fontSize: '10px', color: getCurrentSession().color, fontWeight: 700, fontFamily: 'monospace' }}>
                            {getCurrentSession().name}
                        </Typography>
                    </Box>
                </Box>

                {/* RIGHT: CQG-Style Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Account Selector */}
                    <Button
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '12px' }} />}
                        onClick={handleAccountClick}
                        sx={{
                            background: '#1a1a1a',
                            border: '1px solid #374151',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '10px',
                            padding: '5px 10px',
                            textTransform: 'none',
                            minHeight: '26px',
                            fontFamily: 'monospace',
                            letterSpacing: '0.3px',
                            '&:hover': {
                                background: '#262626',
                                borderColor: '#4b5563'
                            }
                        }}
                    >
                        💼 {selectedAccount
                            ? `${selectedAccount.accountNumber || selectedAccount.account_number || 'N/A'}`
                            : 'SELECT'
                        }
                    </Button>
                    <Menu
                        anchorEl={accountMenuAnchor}
                        open={Boolean(accountMenuAnchor)}
                        onClose={handleAccountClose}
                        slotProps={{
                            paper: {
                                sx: {
                                    background: '#1a1a1a',
                                    border: '1px solid #374151',
                                    color: 'white',
                                    minWidth: 220
                                }
                            }
                        }}
                    >
                        {userAccounts.length === 0 ? (
                            <MenuItem disabled>No accounts available</MenuItem>
                        ) : (
                            userAccounts.map((account) => (
                                <MenuItem
                                    key={account.accountNumber || account.account_number}
                                    onClick={() => handleAccountSelect(account)}
                                    selected={
                                        selectedAccount?.accountNumber === account.accountNumber ||
                                        selectedAccount?.account_number === account.account_number
                                    }
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '11px',
                                        '&:hover': {
                                            background: '#262626'
                                        },
                                        '&.Mui-selected': {
                                            background: '#dc2626',
                                            '&:hover': {
                                                background: '#b91c1c'
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#ffffff' }}>
                                            {account.accountNumber || account.account_number}
                                        </Typography>
                                        <Typography sx={{ color: '#9ca3af', fontSize: '10px' }}>
                                            {account.broker || 'Unknown'} • {account.accountType || 'N/A'}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Menu>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* QuantX Analytics Toggle */}
                        <Tooltip title={showQuantXPanel ? 'Hide QuantX Analytics' : 'Show QuantX Analytics'} arrow>
                            <IconButton
                                onClick={() => setShowQuantXPanel(!showQuantXPanel)}
                                size="small"
                                sx={{
                                    background: showQuantXPanel ? '#dc2626' : '#1a1a1a',
                                    border: '1px solid #dc2626',
                                    color: showQuantXPanel ? '#fff' : '#dc2626',
                                    width: '26px',
                                    height: '26px',
                                    fontSize: '14px',
                                    boxShadow: showQuantXPanel ? '0 0 10px rgba(220, 38, 38, 0.5)' : '0 0 6px rgba(220, 38, 38, 0.3)',
                                    '&:hover': {
                                        background: showQuantXPanel ? '#b91c1c' : '#262626',
                                        borderColor: '#dc2626',
                                        boxShadow: '0 0 12px rgba(220, 38, 38, 0.5)'
                                    }
                                }}
                            >
                                <AnalyticsIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>

                        {/* Market Watch - Navigate to Main Dashboard */}
                        <Tooltip title="Switch to Market Watch Dashboard" arrow>
                            <IconButton
                                onClick={() => navigate('/dashboard')}
                                size="small"
                                sx={{
                                    background: '#1a1a1a',
                                    border: '1px solid #dc2626',
                                    color: '#dc2626',
                                    width: '26px',
                                    height: '26px',
                                    fontSize: '16px',
                                    boxShadow: '0 0 6px rgba(220, 38, 38, 0.3)',
                                    '&:hover': {
                                        background: '#262626',
                                        borderColor: '#dc2626',
                                        boxShadow: '0 0 12px rgba(220, 38, 38, 0.5)',
                                        transform: 'scale(1.05)'
                                    }
                                }}
                            >
                                📊
                            </IconButton>
                        </Tooltip>

                        {/* Q.AI Coach - Toggle Inline Chat Panel */}
                        <Tooltip title={showQAIChat ? "Close Q.AI Chat" : "Open Q.AI Chat"} arrow>
                            <IconButton
                                onClick={() => {
                                    setShowQAIChat(!showQAIChat);
                                    setQAIMinimized(false);
                                }}
                                size="small"
                                sx={{
                                    background: showQAIChat
                                        ? 'linear-gradient(135deg, #0f3a4d 0%, #1a4a5d 100%)'
                                        : 'linear-gradient(135deg, #1a1a1a 0%, #0f2a3d 100%)',
                                    border: '2px solid #3deaff',
                                    color: '#3deaff',
                                    width: '32px',
                                    height: '32px',
                                    fontSize: '14px',
                                    boxShadow: showQAIChat
                                        ? '0 0 16px rgba(61, 234, 255, 0.7), inset 0 0 10px rgba(61, 234, 255, 0.2)'
                                        : '0 0 12px rgba(61, 234, 255, 0.5), inset 0 0 8px rgba(61, 234, 255, 0.1)',
                                    animation: showQAIChat ? 'none' : 'qai-pulse 3s ease-in-out infinite',
                                    '@keyframes qai-pulse': {
                                        '0%, 100%': { boxShadow: '0 0 12px rgba(61, 234, 255, 0.5), inset 0 0 8px rgba(61, 234, 255, 0.1)' },
                                        '50%': { boxShadow: '0 0 18px rgba(61, 234, 255, 0.7), inset 0 0 10px rgba(61, 234, 255, 0.15)' }
                                    },
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #1a3040 0%, #0f3a4d 100%)',
                                        borderColor: '#3deaff',
                                        boxShadow: '0 0 20px rgba(61, 234, 255, 0.7)',
                                        transform: 'scale(1.08)',
                                        animation: 'none'
                                    }
                                }}
                            >
                                <SmartToyIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>

                        {/* Strategy Builder */}
                        <Tooltip title="Strategy Builder" arrow>
                            <IconButton
                                onClick={() => navigate('/strategy-builder')}
                                size="small"
                                sx={{
                                    background: '#1a1a1a',
                                    border: '1px solid #22c55e',
                                    color: '#22c55e',
                                    width: '26px',
                                    height: '26px',
                                    fontSize: '14px',
                                    boxShadow: '0 0 6px rgba(34, 197, 94, 0.3)',
                                    '&:hover': {
                                        background: '#262626',
                                        borderColor: '#22c55e',
                                        boxShadow: '0 0 12px rgba(34, 197, 94, 0.5)',
                                        transform: 'scale(1.05)'
                                    }
                                }}
                            >
                                🛠️
                            </IconButton>
                        </Tooltip>

                        {/* Refresh */}
                        <Tooltip title="Refresh" arrow>
                            <IconButton
                                onClick={handleRefresh}
                                size="small"
                                sx={{
                                    background: '#1a1a1a',
                                    border: '1px solid #374151',
                                    color: '#22c55e',
                                    width: '26px',
                                    height: '26px',
                                    '&:hover': {
                                        background: '#262626',
                                        borderColor: '#22c55e',
                                        transform: 'rotate(90deg)',
                                        transition: 'transform 0.3s ease'
                                    }
                                }}
                            >
                                <RefreshIcon sx={{ fontSize: '14px' }} />
                            </IconButton>
                        </Tooltip>

                        {/* Settings */}
                        <Tooltip title="Settings" arrow>
                            <IconButton
                                size="small"
                                sx={{
                                    background: '#1a1a1a',
                                    border: '1px solid #374151',
                                    color: '#9ca3af',
                                    width: '26px',
                                    height: '26px',
                                    '&:hover': {
                                        background: '#262626',
                                        borderColor: '#6b7280',
                                        color: '#ffffff'
                                    }
                                }}
                            >
                                <SettingsIcon sx={{ fontSize: '14px' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Clock + User */}
                    <Box sx={{
                        background: '#1a1a1a',
                        border: '1px solid #374151',
                        padding: '4px 10px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0.2,
                        minWidth: '100px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '11px' }}>🕐</Typography>
                            <Typography sx={{
                                fontSize: '10px',
                                fontFamily: 'Courier New, monospace',
                                fontWeight: 700,
                                color: '#fbbf24',
                                letterSpacing: '0.5px'
                            }}>
                                {currentTime.toLocaleTimeString('en-US', { hour12: false, timeZone: 'GMT' })}
                            </Typography>
                        </Box>
                        <Typography sx={{
                            fontSize: '8px',
                            color: '#6b7280',
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            fontFamily: 'monospace'
                        }}>
                            👤 {currentUser?.username || 'USER'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Main Content Area with QuantX Panel */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', padding: '0 6px 6px 6px', gap: 0.75 }}>
                {/* Strategy Grid (Main Content) */}
                <Box
                    sx={{
                        flex: 1,
                        padding: 1.5,
                        overflow: 'auto',
                        transition: 'all 0.3s ease',
                        minWidth: 0
                    }}
                >
                    {loading ? (
                        // Loading State
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '60vh',
                                gap: 2
                            }}
                        >
                            <CircularProgress size={60} sx={{ color: '#6b7280' }} />
                            <Typography variant="h6" sx={{ color: '#9ca3af' }}>
                                Loading strategies...
                            </Typography>
                        </Box>
                    ) : strategies.length === 0 ? (
                        // Empty State
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '60vh',
                                gap: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="h4" sx={{ color: '#9ca3af', fontWeight: 600 }}>
                                📊 No Active Strategies
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#6b7280', maxWidth: 500 }}>
                                You don't have any active strategies for this account. Create a new strategy to start trading.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/dashboard')}
                                sx={{
                                    marginTop: 2,
                                    background: '#1a1a1a',
                                    border: '2px solid #991b1b',
                                    color: '#dc2626',
                                    fontWeight: 700,
                                    '&:hover': {
                                        background: '#262626',
                                        borderColor: '#dc2626',
                                        boxShadow: '0 0 12px rgba(220, 38, 38, 0.3)'
                                    }
                                }}
                            >
                                Go to Dashboard
                            </Button>
                        </Box>
                    ) : (
                        // Strategy Cards Grid - COMPACT (0.75 = 6px gaps)
                        <Grid container spacing={0.75}>
                            {strategies.map((strategy) => (
                                <Grid
                                    item
                                    xs={12}  // 1 column on mobile (100% width)
                                    sm={12}  // 1 column on tablet portrait (100% width)
                                    md={6}   // 2 columns on tablet landscape (50% width)
                                    lg={showQuantXPanel ? 6 : 4}   // 2 or 3 columns based on panel
                                    xl={showQuantXPanel ? 4 : 3}   // 3 or 4 columns based on panel
                                    key={strategy.strategyId}
                                >
                                    <StrategyCard
                                        strategyId={strategy.strategyId}
                                        strategyName={strategy.strategyName}
                                        strategyData={strategy}
                                        token={token}
                                        onBuySpread={handleBuySpread}
                                        onSellSpread={handleSellSpread}
                                        onCloseBuy={handleCloseBuy}
                                        onCloseSell={handleCloseSell}
                                        onCloseAll={handleCloseAll}
                                        onResetStrategy={handleResetStrategy}
                                        onApplyParameters={handleApplyParameters}
                                        formatWithPrecision={formatWithPrecision}
                                        realTimePnL={strategy.performance?.pnLBreakdown?.netPnL || 0}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {/* Last Refresh Timestamp */}
                    {!loading && strategies.length > 0 && (
                        <Box
                            sx={{
                                marginTop: 3,
                                textAlign: 'center',
                                color: '#6b7280',
                                fontSize: '0.875rem'
                            }}
                        >
                            Last updated: {lastRefresh.toLocaleTimeString()}
                        </Box>
                    )}
                </Box>

                {/* Q.AI Chat Panel - Flexible Middle Section */}
                {showQAIChat && (
                    <Box
                        sx={{
                            // Flex-based sizing for better responsiveness
                            flex: qaiMaximized ? 1 : '0 0 auto',
                            width: qaiMinimized
                                ? '48px'
                                : qaiMaximized
                                    ? 'auto'
                                    : { xs: '100%', md: '340px', lg: '380px', xl: '400px' },
                            minWidth: qaiMinimized ? '48px' : { md: '300px' },
                            maxWidth: qaiMaximized ? 'none' : { md: '420px' },
                            borderLeft: '2px solid #3deaff',
                            backgroundColor: '#0A0A0F',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            transition: 'width 0.3s ease, flex 0.3s ease',
                            boxShadow: '-2px 0 12px rgba(61, 234, 255, 0.1)'
                        }}
                    >
                        <QAIChatPanel
                            isOpen={showQAIChat}
                            isMinimized={qaiMinimized}
                            isMaximized={qaiMaximized}
                            onClose={() => setShowQAIChat(false)}
                            onMinimize={() => setQAIMinimized(!qaiMinimized)}
                            onMaximize={() => {
                                setQAIMaximized(!qaiMaximized);
                                // When maximizing, hide QuantX panel
                                if (!qaiMaximized) {
                                    setShowQuantXPanel(false);
                                }
                            }}
                            token={token}
                            // INT-1 & INT-2: Pass AI context and symbol for enhanced Q.AI responses
                            aiContext={aiContext}
                            symbol={quantXSymbol}
                        />
                    </Box>
                )}

                {/* QuantX Analytics Right Panel - Institutional Grade Width */}
                {showQuantXPanel && !qaiMaximized && (
                    <Box
                        sx={{
                            width: { xs: '100%', md: '360px', lg: '400px', xl: '420px' },
                            minWidth: { md: '340px', lg: '380px' },
                            maxWidth: '420px',
                            borderLeft: '2px solid #374151',
                            backgroundColor: '#0a0a0a',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.5)',
                            padding: '8px 8px 8px 0'
                        }}
                    >
                        {/* Panel Toggle Tab */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '-20px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 10
                            }}
                        >
                            <IconButton
                                onClick={() => setShowQuantXPanel(false)}
                                size="small"
                                sx={{
                                    backgroundColor: '#1a1a1a',
                                    border: '1px solid #374151',
                                    borderRight: 'none',
                                    borderRadius: '4px 0 0 4px',
                                    color: '#6b7280',
                                    padding: '4px 2px',
                                    '&:hover': {
                                        backgroundColor: '#262626',
                                        color: '#dc2626'
                                    }
                                }}
                            >
                                <ChevronRightIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>

                        {/* QuantX Panel Content - Data Dense Layout */}
                        <Box sx={{ flex: 1, overflow: 'auto', padding: '8px' }}>
                            <DataDensePanel
                                symbol={quantXSymbol}
                                token={token}
                                autoRefresh={true}
                                refreshInterval={QUANTX_CONFIG.refreshInterval}
                                onSymbolChange={setQuantXSymbol}
                            />
                        </Box>
                    </Box>
                )}

                {/* Collapsed Panel Toggle (when panel is hidden) */}
                {!showQuantXPanel && (
                    <Box
                        sx={{
                            position: 'fixed',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1000
                        }}
                    >
                        <Tooltip title="Show QuantX Analytics" placement="left" arrow>
                            <IconButton
                                onClick={() => setShowQuantXPanel(true)}
                                sx={{
                                    backgroundColor: '#dc2626',
                                    border: '1px solid #dc2626',
                                    borderRight: 'none',
                                    borderRadius: '4px 0 0 4px',
                                    color: '#fff',
                                    padding: '8px 4px',
                                    boxShadow: '0 0 10px rgba(220, 38, 38, 0.5)',
                                    '&:hover': {
                                        backgroundColor: '#b91c1c'
                                    }
                                }}
                            >
                                <ChevronLeftIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Footer - QTRADEVIEW Style */}
            <Box
                sx={{
                    height: '28px',
                    backgroundColor: '#0a0a0a',
                    borderTop: '1px solid #374151',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 16px',
                    flexShrink: 0
                }}
            >
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: '#6b7280',
                        fontFamily: 'monospace',
                        letterSpacing: '0.5px'
                    }}
                >
                    QTRADEVIEW © 2024 | Artificial Intelligence SolutionsX
                </Typography>
            </Box>
        </Box>
    );
};

export default AlgoAnalyticsDashboard;
